import { Navbar } from "../navbar/navbar";
import { EventHandler, WithDelegateTarget } from "../../utils/event-handler";
import { onTransitionEnd, reflow } from "../../utils";
import { EVENT_CLICK, EVENT_MOUSEENTER, EVENT_MOUSELEAVE } from "../../utils/events";

export class Menu {
    #SELECTOR_NAVBAR = '.navbar';
    #SELECTOR_MENU = '.menu';
    #SELECTOR_NAV = `${this.#SELECTOR_MENU}__nav`;
    #SELECTOR_BRANCH = `${this.#SELECTOR_MENU}__branch`;
    #SELECTOR_ROOT = `${this.#SELECTOR_BRANCH}--root`;
    #SELECTOR_SUBMENU = `${this.#SELECTOR_MENU}__submenu`;
    #SELECTOR_ITEM = `${this.#SELECTOR_MENU}__link`;
    #SELECTOR_ITEM_HAS_SUBMENU = `${this.#SELECTOR_ITEM}[aria-haspopup="true"][aria-controls]`;
    #SELECTOR_ITEM_BACK = `${this.#SELECTOR_ITEM}--back`;

    #CLASS_NAME_ITEM_L0 = 'menu__link--l0';
    #CLASS_NAME_MENU_OPEN = 'open';
    #CLASS_NAME_ITEM_OPEN = 'open';
    #CLASS_NAME_ANIMATING = 'animating';

    #PROPERTY_NAVBAR_MOBILE = '--navbar-mobile';
    #PROPERTY_MENU_HEIGHT = '--menu-height';

    static EVENT_MENU_SHOW = 'menu-show';
    static EVENT_MENU_SHOWN = 'menu-shown';
    static EVENT_MENU_HIDE = 'menu-hide';
    static EVENT_MENU_HIDDEN = 'menu-hidden';

    #navbar: HTMLElement;
    #menu: HTMLElement;
    #nav: HTMLElement;

    get #mobileBehaviour(): boolean {
        return getComputedStyle(this.#navbar).getPropertyValue(this.#PROPERTY_NAVBAR_MOBILE) === 'true';
    };
    #delayShowHideTimeout: ReturnType<typeof setTimeout>;

    constructor() {
        this.#initElements();
        this.#initEvents();
    }

    #initElements(): void {
        this.#navbar = document.querySelector(this.#SELECTOR_NAVBAR);
        this.#menu = document.querySelector(this.#SELECTOR_MENU);
        this.#nav = document.querySelector(this.#SELECTOR_NAV);

        this.#menu.querySelectorAll<HTMLElement>(this.#SELECTOR_ITEM_HAS_SUBMENU).forEach((item) => {
            this.#setAriaExpanded(item, this.#isShown(this.#getMenu(item)));
        });
    }

    #initEvents(): void {
        EventHandler.on(this.#menu, EVENT_CLICK, this.#SELECTOR_ITEM_HAS_SUBMENU, (event: WithDelegateTarget<MouseEvent>) => {
            if (this.#mobileBehaviour) {
                this.show(this.#getMenu(<HTMLElement>event.delegateTarget));
            }
        });


        EventHandler.on(this.#menu, EVENT_MOUSEENTER, this.#SELECTOR_ITEM_HAS_SUBMENU, (event: WithDelegateTarget<MouseEvent>) => {
            if (!this.#mobileBehaviour) {
                this.#delayShow(this.#getMenu(<HTMLElement>event.delegateTarget));
            }
        });

        EventHandler.on(this.#menu, EVENT_MOUSELEAVE, this.#SELECTOR_ITEM_HAS_SUBMENU, (event: WithDelegateTarget<MouseEvent>) => {
            if (!this.#mobileBehaviour) {
                const target = <HTMLElement>event.delegateTarget;

                this.#delayHide(this.#getMenu(target), target.classList.contains(this.#CLASS_NAME_ITEM_L0) ? undefined : 600);
            }
        });

        EventHandler.on(this.#menu, EVENT_MOUSEENTER, this.#SELECTOR_SUBMENU, (event: WithDelegateTarget<MouseEvent>) => {
            if (!this.#mobileBehaviour) {
                clearTimeout(this.#delayShowHideTimeout);
            }
        });

        EventHandler.on(this.#menu, EVENT_MOUSELEAVE, this.#SELECTOR_SUBMENU, (event: WithDelegateTarget<MouseEvent>) => {
            if (!this.#mobileBehaviour) {
                this.#delayHide(<HTMLElement>event.delegateTarget);
            }
        });


        EventHandler.on(this.#menu, EVENT_CLICK, this.#SELECTOR_ITEM_BACK, (event: WithDelegateTarget<MouseEvent>) => {
            this.hide((<HTMLElement>event.delegateTarget).closest(`${this.#SELECTOR_SUBMENU}`));
        });

        EventHandler.on(document, Navbar.EVENT_NAVBAR_SHOW, () => {
            this.#menu.querySelector(this.#SELECTOR_ROOT).classList.add(this.#CLASS_NAME_MENU_OPEN);
        });

        EventHandler.on(document, Navbar.EVENT_NAVBAR_HIDDEN, () => {
            this.hide(
                this.#menu.querySelector(`${this.#SELECTOR_SUBMENU}.${this.#CLASS_NAME_MENU_OPEN}`)
            );

            this.#menu.querySelector(this.#SELECTOR_ROOT).classList.remove(this.#CLASS_NAME_MENU_OPEN);

            this.#resetMenuHeightProperty();
        });
    }

    #getMenu(item: HTMLElement): HTMLElement {
        return document.getElementById(item.getAttribute('aria-controls'));
    }

    #getItem(menu: HTMLElement): HTMLElement {
        return document.querySelector(`${this.#SELECTOR_ITEM_HAS_SUBMENU}[aria-controls="${menu.id}"]`);
    }

    #isShown(menu: HTMLElement): boolean {
        return menu.classList.contains(this.#CLASS_NAME_MENU_OPEN);
    }

    #setAriaExpanded(item: HTMLElement, isOpen: boolean): void {
        item.setAttribute('aria-expanded', String(isOpen));
    }

    toggle(menu: HTMLElement): void {
        if (this.#isShown(menu)) {
            this.hide(menu);
        } else {
            this.show(menu);
        }
    }

    show(menu: HTMLElement): void {
        if (!menu || this.#isShown(menu)) {
            return;
        }

        this.hide(
            menu.closest(this.#SELECTOR_BRANCH)
                .querySelector(`${this.#SELECTOR_SUBMENU}.${this.#CLASS_NAME_MENU_OPEN}`)
        );

        EventHandler.trigger(menu, Menu.EVENT_MENU_SHOW);

        const item = this.#getItem(menu);
        item.classList.add(this.#CLASS_NAME_ITEM_OPEN);

        menu.classList.add(this.#CLASS_NAME_ANIMATING);

        reflow(menu);

        this.#setNavHeight(menu);
        this.#setSubmenuHeight(menu);

        onTransitionEnd(menu, () => {
            menu.classList.remove(this.#CLASS_NAME_ANIMATING);
            this.#resetSubmenuHeight(menu);

            EventHandler.trigger(menu, Menu.EVENT_MENU_SHOWN);
        });

        menu.classList.add(this.#CLASS_NAME_MENU_OPEN);

        this.#setAriaExpanded(item, true);
    }

    hide(menu: HTMLElement): void {
        if (!menu || !this.#isShown(menu)) {
            return;
        }

        this.hide(
            menu.querySelector(`${this.#SELECTOR_SUBMENU}.${this.#CLASS_NAME_MENU_OPEN}`)
        );

        EventHandler.trigger(menu, Menu.EVENT_MENU_HIDE);

        const item = this.#getItem(menu);
        item.classList.remove(this.#CLASS_NAME_ITEM_OPEN);

        this.#setSubmenuHeight(menu);
        menu.classList.add(this.#CLASS_NAME_ANIMATING);

        this.#setNavHeight(menu, false);

        onTransitionEnd(menu, () => {
            menu.classList.remove(this.#CLASS_NAME_ANIMATING);
            this.#resetSubmenuHeight(menu);

            EventHandler.trigger(menu, Menu.EVENT_MENU_HIDDEN);
        });

        reflow(menu);
        this.#setSubmenuHeight(menu, 0);
        menu.classList.remove(this.#CLASS_NAME_MENU_OPEN);

        this.#setAriaExpanded(item, false);
    }

    #setNavHeight(menu: HTMLElement, forward: boolean = true): void {
        if (this.#mobileBehaviour) {
            this.#setMobileNavHeight(menu, forward);
        } else {
            this.#setDesktopNavHeight(menu, forward);
        }
    }

    #setMobileNavHeight(menu: HTMLElement, forward: boolean): void {
        const previousMenu: HTMLElement = menu.parentElement.closest(`${this.#SELECTOR_SUBMENU}.${this.#CLASS_NAME_MENU_OPEN}`);

        if (forward) {
            if (!previousMenu) {
                this.#setMenuHeightProperty(this.#getNavHeight());
            }

            this.#setMenuHeightProperty(menu.offsetHeight);
        } else {
            this.#setMenuHeightProperty(previousMenu?.offsetHeight ?? this.#getNavHeight());
        }
    }

    #setDesktopNavHeight(menu: HTMLElement, forward: boolean): void {
        if (!forward) {
            return;
        }

        const previousMenu: HTMLElement = menu.parentElement.closest(`${this.#SELECTOR_SUBMENU}.${this.#CLASS_NAME_MENU_OPEN}`);

        if (previousMenu) {
            return;
        }

        this.#setMenuHeightProperty(this.#getMenuBranchHeight(menu));
    }

    #getMenuBranchHeight(menu: HTMLElement): number {
        const branch: HTMLElement = menu.querySelector(this.#SELECTOR_BRANCH);
        const isVisible = menu.classList.contains(this.#CLASS_NAME_ANIMATING);

        if (!isVisible) {
            menu.classList.add(this.#CLASS_NAME_ANIMATING);
        }
        branch.style.height = 'auto';

        const submenus = menu.querySelectorAll<HTMLElement>(this.#SELECTOR_SUBMENU);
        const height = Math.max(
            branch.offsetHeight,
            ...Array.from(submenus).map((submenu) => this.#getMenuBranchHeight(submenu))
        );

        branch.style.height = '';

        if (!isVisible) {
            menu.classList.remove(this.#CLASS_NAME_ANIMATING);
        }

        return height;
    }

    #setMenuHeightProperty(height?: number): void {
        this.#menu.style.setProperty(this.#PROPERTY_MENU_HEIGHT, height ? `${height}px` : '');
    }

    #resetMenuHeightProperty(): void {
        this.#menu.style.setProperty(this.#PROPERTY_MENU_HEIGHT, '');
    }

    #getNavHeight(): number {
        this.#nav.style.height = 'auto';
        const height = this.#nav.offsetHeight;
        this.#nav.style.height = '';
        reflow(this.#nav);

        return height;
    }

    #setSubmenuHeight(menu: HTMLElement, height?: number): void {
        if (this.#mobileBehaviour) {
            return;
        }

        const previousMenu: HTMLElement = menu.parentElement.closest(`${this.#SELECTOR_SUBMENU}.${this.#CLASS_NAME_MENU_OPEN}`);

        if (previousMenu) {
            return;
        }

        this.#setMenuHeight(menu, height === undefined ? this.#getMenuHeight(menu) : height);
    }

    #resetSubmenuHeight(menu: HTMLElement): void {
        if (this.#mobileBehaviour) {
            return;
        }

        const previousMenu: HTMLElement = menu.parentElement.closest(`${this.#SELECTOR_SUBMENU}.${this.#CLASS_NAME_MENU_OPEN}`);

        if (previousMenu) {
            return;
        }

        this.#resetMenuHeight(menu);
    }

    #setMenuHeight(menu: HTMLElement, height?: number): void {
        menu.style.height = height !== undefined ? `${height}px` : '';
    }

    #resetMenuHeight(menu: HTMLElement): void {
        menu.style.height = '';
    }

    #getMenuHeight(menu: HTMLElement): number {
        menu.style.height = 'auto';
        const height = menu.offsetHeight;
        menu.style.height = '';
        reflow(menu);

        return height;
    }

    #delayShow(menu: HTMLElement): void {
        clearTimeout(this.#delayShowHideTimeout);

        if (this.#isShown(menu)) {
            return;
        }

        const openMenus = this.#menu.querySelectorAll<HTMLElement>(`${this.#SELECTOR_SUBMENU}.${this.#CLASS_NAME_MENU_OPEN}`);

        if (Array.from(openMenus).some((openMenu) => (
            openMenu === menu || openMenu.contains(menu)
        ))) {
            this.show(menu);
        } else {
            this.#delayShowHideTimeout = setTimeout(() => {
                this.show(menu);
            }, 300);
        }
    }

    #delayHide(menu: HTMLElement, delay: number = 300): void {
        clearTimeout(this.#delayShowHideTimeout);

        this.#delayShowHideTimeout = setTimeout(() => {
            this.hide(menu);
        }, delay);
    }
}
